import React, { useEffect, useRef, useState } from 'react';
import SEO from '../../../components/SEO';
import TrainingHeader from '../components/TrainingHeader';
import TrainingMedia from '../components/TrainingMedia';
import OfferCard from '../components/OfferCard';
import TrainingComparator from '../components/TrainingComparator';
import { trackTrainingEvent } from '../trainingAnalytics';
import { getCatalogueContent } from '../trainingContent';
import { getComparatorRecommendation } from '../trainingComparator';
import { TRAINING_OFFERS } from '../trainingOffers';
import { getAlternatePaths, getCataloguePath } from '../trainingRoutes';
import { buildCatalogueStructuredData } from '../trainingSeo';
import '../training.css';

const EMPTY_COMPARATOR_ANSWERS = Object.freeze({
  goal: '',
  time: '',
  experience: '',
});

export default function TrainingCataloguePage({ locale }) {
  const content = getCatalogueContent(locale);
  const path = getCataloguePath(locale);
  const trackedPathRef = useRef();
  const [comparatorAnswers, setComparatorAnswers] = useState(EMPTY_COMPARATOR_ANSWERS);
  const [comparatorStep, setComparatorStep] = useState(1);
  const recommendation = getComparatorRecommendation(comparatorAnswers);
  const visibleRecommendation = comparatorStep === 4 ? recommendation : undefined;

  useEffect(() => {
    if (path && trackedPathRef.current !== path) {
      trackedPathRef.current = path;
      trackTrainingEvent('catalog_view', { locale, path });
    }
  }, [locale, path]);

  if (!content || !path) {
    throw new TypeError(`Unsupported training locale: ${locale}`);
  }

  return (
    <div className="training-page training-catalogue">
      <SEO
        {...content.seo}
        path={path}
        alternates={getAlternatePaths(path)}
        structuredData={buildCatalogueStructuredData(locale)}
      />
      <TrainingHeader locale={locale} />
      <main className="training-catalogue__main">
        <section className="training-catalogue__hero" aria-labelledby="catalogue-title">
          <TrainingMedia src={content.seo.image} alt={content.seo.title} eager />
          <p className="training-catalogue__eyebrow">{content.eyebrow}</p>
          <h1 id="catalogue-title">{content.title}</h1>
          <p className="training-catalogue__introduction">{content.introduction}</p>
        </section>

        <TrainingComparator
          locale={locale}
          content={content}
          answers={comparatorAnswers}
          step={comparatorStep}
          recommendation={recommendation}
          onChange={setComparatorAnswers}
          onStepChange={setComparatorStep}
        />

        <section
          className="training-catalogue__grid"
          id="catalogue-offers"
          aria-label={content.offersLabel}
        >
          {TRAINING_OFFERS.map((offer, index) => (
            <OfferCard
              key={offer.key}
              offer={offer}
              locale={locale}
              position={index + 1}
              recommended={visibleRecommendation?.offerKey === offer.key}
            />
          ))}
        </section>

        <section className="training-catalogue__method" aria-labelledby="catalogue-method-title">
          <h2 id="catalogue-method-title">{content.methodTitle}</h2>
          <p>{content.methodSummary}</p>
        </section>
      </main>
    </div>
  );
}
